// Copyright 2025. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    Button,
    Checkbox,
    Popover,
} from '@plesk/ui-library';
import {
    BUTTON_INTENT_TYPE,
    INTENT_TYPE,
} from 'common/constants';
import * as responseErrorActions from 'common/modules/app/responseError/actions';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import { connect } from 'react-redux';
import { RootState } from 'client/core/store';
import { StyledButton } from 'admin/common/components/Button/Styles';
import { COLORS } from 'common/theme';

export const TEST = {
    FORCE_CONFIRM_POPOVER: 'force-confirm-popover',
    FORCE_CONFIRM_BUTTON: 'force-confirm-button',
};

interface IButtonWithForceConfirmationProps extends React.HTMLProps<HTMLDivElement> {
    onClose?: () => void;
    confirmationButtonText?: string | React.ReactNode;
    confirmationButtonGhost?: boolean;
    disabled?: boolean;
    icon?: string | React.ReactNode;
    isLoading?: boolean;
    handleConfirm: (force?: boolean) => void;
    translations: {
        button: React.ReactNode;
        text?: React.ReactNode;
        tooltip?: React.ReactNode;
        title?: React.ReactNode;
        forceLabel?: React.ReactNode;
        forceWarning?: React.ReactNode;
    };
    'data-cy'?: string;
    buttonColor?: string;
    withStyledButton?: boolean;
    buttonSize?: 'md' | 'lg';
    buttonIntent?: BUTTON_INTENT_TYPE;
    buttonClassName?: string;
    placement?: string;
    withConfirmation?: boolean;
    withForceCheckbox?: boolean;
}

export type ButtonWithForceConfirmationProps =
    IButtonWithForceConfirmationProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const ButtonWithForceConfirmation: React.FC<ButtonWithForceConfirmationProps> = ({
    responseErrorActions: { clearResponseError },
    confirmationButtonText,
    confirmationButtonGhost,
    onClose,
    error,
    icon,
    handleConfirm,
    isLoading,
    disabled,
    translations: {
        button,
        text,
        title,
        tooltip,
        forceLabel,
        forceWarning,
    },
    'data-cy': dataCy,
    buttonColor,
    withStyledButton,
    buttonSize,
    buttonIntent,
    buttonClassName,
    placement,
    withConfirmation = true,
    withForceCheckbox = false,
}) => {
    const ghost = confirmationButtonGhost !== undefined ? confirmationButtonGhost : true;
    const [isPopoverOpened, setPopoverOpened] = React.useState(false);
    const [useForce, setUseForce] = React.useState(false);

    const handleClose = () => {
        setPopoverOpened(false);
        setUseForce(false);
        clearResponseError();

        onClose?.();
    };

    const handleOpenPopover = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        clearResponseError();
        setPopoverOpened(true);
    };

    const handleClickConfirm = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setPopoverOpened(false);

        await handleConfirm(withForceCheckbox ? useForce : false);

        if (!error.error) {
            setPopoverOpened(false);
        }
    };

    const handleForceChange = () => {
        setUseForce(!useForce);
    };

    let Btn = Button;
    let style: Record<string, string> = { verticalAlign: 'bottom', maxHeight: '28px' };
    if (withStyledButton) {
        Btn = StyledButton;
        style = {};
    }

    const handleClickDirect = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        await handleConfirm(false);
    };

    return (
        <Popover
            data-cy={TEST.FORCE_CONFIRM_POPOVER}
            onClose={handleClose}
            visible={isPopoverOpened}
            target={
                <Btn
                    intent={buttonIntent}
                    ghost={ghost}
                    size={buttonSize}
                    style={style}
                    state={isLoading ? 'loading' : undefined}
                    disabled={disabled}
                    tooltip={tooltip}
                    onClick={withConfirmation ? handleOpenPopover : handleClickDirect}
                    icon={icon}
                    data-cy={dataCy}
                    className={buttonClassName}
                >
                    {button}
                </Btn>
            }
            placement={placement || 'left'}
        >
            {error.error ? (
                <div style={{ color: '#d32f2f' }}>{error.error}</div>
            ) : (
                <div style={{ padding: '16px', minWidth: '300px' }}>
                    {title && <h4 style={{ margin: '0 0 8px 0' }}>{title}</h4>}
                    {text && <div style={{ margin: '0 0 12px 0' }}>{text}</div>}

                    {withForceCheckbox && (
                        <div style={{ margin: '12px 0' }}>
                            <Checkbox
                                checked={useForce}
                                onChange={handleForceChange}
                                name="force"
                            >
                                {forceLabel}
                            </Checkbox>
                            {useForce && forceWarning && (
                                <div style={{
                                    marginTop: '8px',
                                    padding: '8px',
                                    backgroundColor: '#fff3cd',
                                    border: '1px solid #ffeaa7',
                                    borderRadius: '4px',
                                    fontSize: '0.9em',
                                    color: '#856404',
                                }}>
                                    <strong>⚠️ {forceWarning}</strong>
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
                        <Button
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            data-cy={TEST.FORCE_CONFIRM_BUTTON}
                            intent={INTENT_TYPE.DANGER}
                            state={isLoading ? 'loading' : undefined}
                            disabled={disabled}
                            onClick={handleClickConfirm}
                            style={{ backgroundColor: buttonColor || COLORS.RED_0 }}
                        >
                            {confirmationButtonText || button}
                        </Button>
                    </div>
                </div>
            )}
        </Popover>
    );
};

const mapStateToProps = (state: RootState) => ({
    error: state.app.responseError,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    responseErrorActions: bindActionCreators(responseErrorActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ButtonWithForceConfirmation);
